import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HistoryDeposit.css';

interface DepositTransaction {
  id: string;
  symbol: string;
  amount: string;
  date: string;
  status: 'completed' | 'pending';
  icon: string;
  color: string;
}

const mockDeposits: DepositTransaction[] = [
  {
    id: '1',
    symbol: 'USDT',
    amount: '500.00000000 USDT',
    date: 'Today at 11:23 AM',
    status: 'completed',
    icon: '₮',
    color: '#26a17b'
  },
  {
    id: '2',
    symbol: 'BTC',
    amount: '0.05000000 BTC',
    date: 'Yesterday at 4:46 PM',
    status: 'completed',
    icon: '₿',
    color: '#f7931a'
  },
  {
    id: '3',
    symbol: 'ETH',
    amount: '2.50000000 ETH',
    date: 'Dec 13, 2024 at 2:30 PM',
    status: 'pending',
    icon: 'Ξ',
    color: '#627eea'
  },
  {
    id: '4',
    symbol: 'USDT',
    amount: '1,000.00000000 USDT',
    date: 'Dec 12, 2024 at 10:15 AM',
    status: 'completed',
    icon: '₮',
    color: '#26a17b'
  },
  {
    id: '5',
    symbol: 'SOL',
    amount: '15.00000000 SOL',
    date: 'Dec 11, 2024 at 8:45 AM',
    status: 'completed',
    icon: '◎',
    color: '#14f195'
  },
  {
    id: '6',
    symbol: 'BNB',
    amount: '3.25000000 BNB',
    date: 'Dec 10, 2024 at 5:20 PM',
    status: 'completed',
    icon: '◆',
    color: '#f3ba2f'
  }
];

const HistoryDeposit: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'deposits' | 'withdrawals' | 'swaps'>('deposits');
  const [timeFilter] = useState('Past 7 days');
  const [statusFilter] = useState('All');
  const [activeNav, setActiveNav] = useState<string>('history');

  const handleNavClick = (nav: string) => {
    setActiveNav(nav);
    if (nav === 'home') {
      navigate('/dashboard');
    }
  };

  return (
    <div className="history-deposit-container">
      <header className="history-deposit-header">
        <h1 className="history-deposit-title">History</h1>
      </header>

      <div className="history-deposit-content">
        <div className="history-deposit-tabs">
          <button
            className={`history-deposit-tab ${activeTab === 'deposits' ? 'active' : ''}`}
            onClick={() => setActiveTab('deposits')}
          >
            Deposits
          </button>
          <button
            className={`history-deposit-tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdrawals')}
          >
            Withdrawals
          </button>
          <button
            className={`history-deposit-tab ${activeTab === 'swaps' ? 'active' : ''}`}
            onClick={() => setActiveTab('swaps')}
          >
            Swaps
          </button>
        </div>

        <div className="history-deposit-main">
          <div className="history-deposit-filters">
            <div className="history-deposit-filter-group">
              <label className="history-deposit-filter-label">Time</label>
              <button className="history-deposit-filter-button">
                <span>{timeFilter}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="history-deposit-filter-group">
              <label className="history-deposit-filter-label">Status</label>
              <button className="history-deposit-filter-button">
                <span>{statusFilter}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="history-deposit-list">
            {mockDeposits.map((transaction) => (
              <button key={transaction.id} className="history-deposit-item">
                <div className="history-deposit-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3V21M12 21L5 14M12 21L19 14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="history-deposit-item-info">
                  <div className="history-deposit-item-row">
                    <span className="history-deposit-item-symbol">{transaction.symbol}</span>
                    <span className="history-deposit-item-amount">{transaction.amount}</span>
                  </div>
                  <div className="history-deposit-item-row">
                    <span className="history-deposit-item-date">{transaction.date}</span>
                    <span className={`history-deposit-item-status ${transaction.status}`}>
                      {transaction.status === 'completed' ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <nav className="history-deposit-nav">
        <button
          className={`nav-item ${activeNav === 'home' ? 'active' : ''}`}
          onClick={() => handleNavClick('home')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Home</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'markets' ? 'active' : ''}`}
          onClick={() => handleNavClick('markets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 12L18 16L14 12L10 16L6 12L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 6L18 10L14 6L10 10L6 6L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Markets</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'trade' ? 'active' : ''}`}
          onClick={() => handleNavClick('trade')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Trade</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'history' ? 'active' : ''}`}
          onClick={() => handleNavClick('history')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>History</span>
        </button>
        <button
          className={`nav-item ${activeNav === 'assets' ? 'active' : ''}`}
          onClick={() => handleNavClick('assets')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Assets</span>
        </button>
      </nav>
    </div>
  );
};

export default HistoryDeposit;